/**
 * Adaptateur API Client - Unifie les appels entre contexte Admin et Extranet
 *
 * Permet aux composants d'utiliser une interface unique tout en appelant
 * les bons endpoints selon le contexte utilisateur (admin ou extranet).
 *
 * Usage:
 *   import { createClientApi } from '@/api/clientApiAdapter';
 *   const api = createClientApi(clientId, isExtranet);
 *   const data = await api.getData();
 */

import Resource from "@/api/resource";

/**
 * Mapping des endpoints Admin vs Extranet
 */
const ENDPOINTS = {
  // Données client
  data: {
    admin: (id) => `comptes/${id}`,
    extranet: () => 'extranet/data'
  },
  updateData: {
    admin: (id) => `comptes/${id}`,
    extranet: () => 'extranet/data/update'
  },
  updateFact: {
    admin: (id) => `comptes/${id}/updateFact`,
    extranet: () => 'extranet/data/updateFact'
  },
  updateFactFromMainAdd: {
    admin: (id) => `comptes/${id}/updateFactFromMainAdd`,
    extranet: () => 'extranet/data/updateFactFromMainAdd'
  },

  // Services
  services: {
    admin: (id) => `comptes/${id}/service`,
    extranet: (id) => `extranet/services/${id}`
  },

  // Factures
  receipts: {
    admin: (id) => `comptes/${id}/receipts`,
    extranet: (id) => `extranet/receipt/${id}`
  },

  // Comptes SIP
  sipAccounts: {
    admin: (id) => `comptes/${id}/SIPAccounts`,
    extranet: (id) => `extranet/service/sipaccounts/${id}`
  },
  sipAccountUpdate: {
    admin: () => 'sipaccounts',
    extranet: () => 'extranet/service/sipaccounts'
  },
  sipBillNames: {
    admin: (id) => `sipaccounts/getPhoneBillName/${id}`,
    extranet: (id) => `extranet/service/sipaccounts/getPhoneBillName/${id}`
  },
  sipLogs: {
    admin: (sipId) => `sipaccounts/${sipId}/getSipLogs`,
    extranet: (sipId) => `extranet/service/sipaccounts/${sipId}/getSipLogs`
  },
  sipIssues: {
    admin: (id) => `comptes/${id}/SIPAccounts/issues`,
    extranet: () => 'extranet/service/sipaccounts/issues'
  },
  sipIssuesCount: {
    admin: (id) => `comptes/${id}/SIPAccounts/issues/count`,
    extranet: () => 'extranet/service/sipaccounts/issues/count'
  },

  // Annuaires / Phonebooks
  phonebooks: {
    admin: (id) => `phonebook/getGroupByIdCli/${id}`,
    extranet: (id) => `extranet/service/phonebooks/${id}`
  },
  phonebooksByGrp: {
    admin: (id) => `phonebook/byGrp/${id}`,
    extranet: (id) => `extranet/service/phonebooksByGrp/${id}`
  },
  phonebookGroups: {
    admin: (id) => `phonebook/getGroupByIdCli/${id}`,
    extranet: (id) => `extranet/service/phonebook/getGroupByIdCli/${id}`
  },
  phonebookGroupsByGrpCli: {
    admin: (id) => `phonebook/getGroupByIdGrpCli/${id}`,
    extranet: (id) => `extranet/service/phonebook/getGroupByIdGrpCli/${id}`
  },
  phonebookCreateGroup: {
    admin: () => 'phonebook/createGroup',
    extranet: () => 'extranet/service/phonebook/createGroupByIdCli'
  },
  phonebookRenameGroup: {
    admin: () => 'phonebook/renameGroup',
    extranet: () => 'extranet/service/phonebook/renameGroupByIdGrp'
  },
  phonebookDeleteGroup: {
    admin: (grpId) => `phonebook/deleteGroup/${grpId}`,
    extranet: (grpId) => `extranet/service/phonebook/deleteGroupByIdGrp/${grpId}`
  },
  phonebookTruncateGroup: {
    admin: (id) => `phonebook/truncateGroup/${id}`,
    extranet: (id) => `extranet/service/phonebooksByGrp/truncateGroupByIdGrp/${id}/grp`
  },
  phonebookMoveContact: {
    admin: () => 'phonebook/moveContact',
    extranet: () => 'extranet/service/phonebook/moveContact'
  },
  phonebookDownloadCSV: {
    admin: (id) => `phonebook/dlSipContactCSV/${id}`,
    extranet: (id) => `extranet/service/phonebook/dlSipContactCSVByIdGrpCli/${id}`
  },
  phonebookUpload: {
    admin: (id) => `phonebook/upload/${id}`,
    extranet: (id) => `extranet/service/phonebook/upload/${id}`
  },
  phonebookCheckExists: {
    admin: (name) => `phonebook/checkIfAlreadyExists/${name}`,
    extranet: (name) => `extranet/service/phonebook/checkIfAlreadyExists/${name}`
  },
  phonebookSharedDatas: {
    admin: (cptId, grpId) => `phonebook/byGrp/${cptId}/getSharedDatas/${grpId}`,
    extranet: (cptId, grpId) => `extranet/service/phonebooksByGrp/${cptId}/getSharedDatas/${grpId}`
  },
  phonebookDonateAccess: {
    admin: (cptId) => `phonebook/donateAccessTo/${cptId}`,
    extranet: (cptId) => `extranet/service/phonebooksByGrp/donateAccessTo/${cptId}`
  },
  phonebookRemoveAccess: {
    admin: (cptId) => `phonebook/removeAccessTo/${cptId}`,
    extranet: (cptId) => `extranet/service/phonebooksByGrp/removeAccessTo/${cptId}`
  },

  // CDR / Consommations
  cdrTelFixeUsage: {
    admin: (id) => `cdr/getTelFixeUsage/${id}`,
    extranet: (id) => `extranet/service/cdr/getTelFixeUsage/${id}`
  },
  cdrMobileOverview: {
    admin: (id) => `cdr/getOverviewMobileUsage/${id}`,
    extranet: (id) => `extranet/service/cdr/getOverviewMobileUsage/${id}`
  },
  cdrMobileData: {
    admin: (id) => `cdr/getMobileDataUsage/${id}`,
    extranet: (id) => `extranet/service/cdr/getMobileDataUsage/${id}`
  },

  // E-Campagne
  ecampService: {
    admin: (id) => `comptes/${id}/ecamp`,
    extranet: () => 'extranet/service/ecamp'
  },

  // Support (extranet uniquement, mais peut être étendu)
  support: {
    admin: () => 'support/send', // Si implémenté côté admin
    extranet: () => 'extranet/support/send'
  },

  groupSipBillNames: {
    admin: (id) => `sipaccounts/getPhoneBillNameByGrp/${id}`,
    extranet: (id) => `extranet/service/sipaccounts/getPhoneBillNameByGrp/${id}`
  },

  // Contacts SIP (endpoint_contacts)
  contactApi: {
    admin: (id) => `comptes/${id}/SIPLogs/endpointContacts`,
    extranet: (id) => `extranet/service/sipaccounts/${id}/endpointContacts`,
    statusHistory: (sipId) => `comptes/${sipId}/SIPLogs/contactStatusHistory`,
    extranetStatusHistory: (sipId) => `extranet/service/sipaccounts/${sipId}/SIPLogs/contactStatusHistory`,
    rebootContact: (clientId, sipId) => `comptes/${clientId}/SIPAccounts/${sipId}/SIPLogs/rebootContact`,
  },

  // Déploiements Asterisk
  asteriskDeploy: {
    admin: () => 'asterisk/deploy-full',
    extranet: () => 'asterisk/deploy-full',
  },
  asteriskDeployments: {
    admin: () => 'asterisk/deployments',
    extranet: () => 'asterisk/deployments',
  },
  asteriskRollback: {
    admin: (id) => `asterisk/rollback/${id}`,
    extranet: (id) => `asterisk/rollback/${id}`,
  },

  // Renvois d'appels (Diversions SIP)
  sipDiversions: {
    admin: (id, sipAccountId) => `clients/${id}/SIPAccountsDiversions/${sipAccountId}`,
    extranet: (id, sipAccountId) => `extranet/clients/${id}/SIPAccountsDiversions/${sipAccountId}`,
  },
  sipDiversionsShow: {
    admin: (id, sipAccountId) => `clients/${id}/SIPAccountsDiversions/${sipAccountId}/show`,
    extranet: (id, sipAccountId) => `extranet/clients/${id}/SIPAccountsDiversions/${sipAccountId}/show`,
  },
  sipDiversionsData: {
    admin: (id, sipAccountId) => `clients/${id}/SIPAccountsDiversions/${sipAccountId}/data`,
    extranet: (id, sipAccountId) => `extranet/clients/${id}/SIPAccountsDiversions/${sipAccountId}/data`,
  },

  // Failover (renvoi d'urgence)
  failover: {
    admin: (id) => `comptes/${id}/numbers`,
    extranet: (id) => `extranet/failover/${id}`
  },
  failoverUpdate: {
    admin: (number) => `telephonie/failover/${number}`,
    extranet: (id, number) => `extranet/failover/${id}/${number}`
  },
  failoverBatch: {
    admin: (id) => `telephonie/failover-batch/${id}`,
    extranet: (id) => `extranet/failover-batch/${id}`
  },

  // GED Documents
  documents: {
    admin: (id) => `comptes/${id}/documents`,
    extranet: (id) => `extranet/documents/${id}`
  },
  documentItem: {
    admin: (id, docId) => `comptes/${id}/documents/${docId}`,
    extranet: (id, docId) => `extranet/documents/${id}/${docId}`
  },
  documentDownload: {
    admin: (id, docId) => `comptes/${id}/documents/${docId}/download`,
    extranet: (id, docId) => `extranet/documents/${id}/${docId}/download`
  },
  documentPreview: {
    admin: (id, docId) => `comptes/${id}/documents/${docId}/preview`,
    extranet: (id, docId) => `extranet/documents/${id}/${docId}/preview`
  },
  documentSoundFiles: {
    admin: (id) => `comptes/${id}/documents/sound-files`,
    extranet: (id) => `extranet/documents/${id}/sound-files`
  },
  documentSharedSummary: {
    admin: (id, childId) => `comptes/${id}/documents/shared-summary/${childId}`,
    extranet: (id, childId) => `extranet/documents/${id}/shared-summary/${childId}`
  }
};

/**
 * Détermine si l'utilisateur actuel est en contexte extranet
 * @param {Object} store - Vuex store (optionnel)
 * @returns {boolean}
 */
export function isExtranetContext(store = null) {
  if (store) {
    if (typeof store.getters['user/isExtranet'] === 'boolean') {
      return store.getters['user/isExtranet'];
    }
    // Repli : le côté se lit au préfixe du rôle, pas à un nom précis — les
    // quatre profils s'appellent tous « extranet … ».
    const roles = store.getters['user/roles'] || [];
    return roles.some(r => typeof r === 'string' && r.startsWith('extranet'));
  }
  // Fallback: vérifier l'URL
  return window.location.pathname.startsWith('/extranet');
}

/**
 * Récupère l'endpoint approprié selon le contexte
 * @param {string} endpointKey - Clé de l'endpoint dans ENDPOINTS
 * @param {boolean} isExtranet - true si contexte extranet
 * @param  {...any} args - Arguments à passer au générateur d'endpoint
 * @returns {string} L'URL de l'endpoint
 */
export function getEndpoint(endpointKey, isExtranet, ...args) {
  const endpoint = ENDPOINTS[endpointKey];
  if (!endpoint) {
    console.warn(`Endpoint inconnu: ${endpointKey}`);
    return null;
  }
  const context = isExtranet ? 'extranet' : 'admin';
  return endpoint[context](...args);
}

/**
 * Crée une instance Resource avec le bon endpoint
 * @param {string} endpointKey - Clé de l'endpoint
 * @param {boolean} isExtranet - Contexte extranet
 * @param  {...any} args - Arguments pour l'endpoint
 * @returns {Resource}
 */
export function createResource(endpointKey, isExtranet, ...args) {
  const endpoint = getEndpoint(endpointKey, isExtranet, ...args);
  return new Resource(endpoint);
}

/**
 * Factory pour créer une API client unifiée
 * @param {number|string} clientId - ID du client
 * @param {boolean} isExtranet - true si contexte extranet
 * @returns {Object} API client avec méthodes unifiées
 */
export function createClientApi(clientId, isExtranet = false) {
  const ctx = isExtranet;
  const id = clientId;

  return {
    // ===== Données client =====
    async getData(params = {}) {
      const resource = createResource('data', ctx, id);
      if (ctx) {
        params.id_cli = id;
      }
      return resource.list(params);
    },

    async updateData(data) {
      const resource = createResource('updateData', ctx, id);
      return resource.update(id, data);
    },

    async updateFact(data) {
      const resource = createResource('updateFact', ctx, id);
      return resource.update(id, data);
    },

    // ===== Services =====
    async getServices(params = {}) {
      const resource = createResource('services', ctx, id);
      return resource.list(params);
    },

    // ===== Factures =====
    async getReceipts(params = {}) {
      const resource = createResource('receipts', ctx, id);
      return resource.list(params);
    },


    // ===== Comptes SIP =====
    async getSipAccounts(params = {}) {
      const resource = createResource('sipAccounts', ctx, id);
      return resource.list(params);
    },

    async updateSipAccount(sipId, data) {
      const resource = createResource('sipAccountUpdate', ctx);
      return resource.update(sipId, data);
    },

    async getSipBillNames() {
      const resource = createResource('sipBillNames', ctx, id);
      return resource.list();
    },

    async getSipLogs(sipId, params = {}) {
      const resource = createResource('sipLogs', ctx, sipId);
      return resource.list(params);
    },

    async rebootContact(sipId, data) {
      const resource = createResource('rebootContact', ctx, id, sipId);
      return resource.save(data);
    },

    async deployFull(data) {
      const resource = createResource('asteriskDeploy', ctx);
      return resource.store(data);
    },

    async getDeployments(params = {}) {
      const resource = createResource('asteriskDeployments', ctx);
      return resource.list(params);
    },

    async rollback(deploymentId, data) {
      const resource = createResource('asteriskRollback', ctx, deploymentId);
      return resource.store(data || {});
    },

    async getSipDiversions(sipAccountId, params = {}) {
      const resource = createResource('sipDiversions', ctx, id, sipAccountId);
      return resource.list(params);
    },

    async getSipDiversionsShow(sipAccountId) {
      const resource = createResource('sipDiversionsShow', ctx, id, sipAccountId);
      return resource.get();
    },

    async getSipDiversionsData(sipAccountId) {
      const resource = createResource('sipDiversionsData', ctx, id, sipAccountId);
      return resource.get();
    },

    async saveSipDiversions(sipAccountId, data) {
      const resource = createResource('sipDiversions', ctx, id, sipAccountId);
      return resource.store(data);
    },

    async deleteSipDiversions(sipAccountId) {
      const resource = createResource('sipDiversions', ctx, id, sipAccountId);
      return resource.delete();
    },

    async getSipIssues(params = {}) {
      const resource = createResource('sipIssues', ctx, id);
      return resource.list(params);
    },

    async getSipIssuesCount(params = {}) {
      const resource = createResource('sipIssuesCount', ctx, id);
      return resource.list(params);
    },

    // ===== Annuaires =====
    async getPhonebooks(params = {}) {
      const resource = createResource('phonebooks', ctx, id);
      return resource.list(params);
    },

    async getPhonebooksByGrp(grpId, params = {}) {
      const resource = createResource('phonebooksByGrp', ctx, grpId);
      return resource.list(params);
    },

    async getPhonebookGroups(params = {}) {
      const resource = createResource('phonebookGroups', ctx, id);
      return resource.list(params);
    },

    async createPhonebookGroup(data) {
      const resource = createResource('phonebookCreateGroup', ctx);
      return resource.store(data);
    },

    async renamePhonebookGroup(grpId, data) {
      const resource = createResource('phonebookRenameGroup', ctx);
      return resource.update(`${grpId}/${id}`, data);
    },

    async deletePhonebookGroup(grpId) {
      const resource = createResource('phonebookDeleteGroup', ctx, grpId);
      return resource.destroy(grpId);
    },

    async truncatePhonebookGroup(grpId) {
      const resource = createResource('phonebookTruncateGroup', ctx, grpId);
      return resource.destroy(grpId);
    },

    async movePhonebookContact(contactId, data) {
      const resource = createResource('phonebookMoveContact', ctx);
      return resource.update(`${contactId}/${id}`, data);
    },

    async downloadPhonebookCSV(params = {}) {
      const resource = createResource('phonebookDownloadCSV', ctx, id);
      return resource.list(params);
    },

    getPhonebookUploadUrl() {
      return `/api/${getEndpoint('phonebookUpload', ctx, id)}`;
    },

    // ===== CDR / Consommations =====
    async getTelFixeUsage(params = {}) {
      const resource = createResource('cdrTelFixeUsage', ctx, id);
      return resource.list(params);
    },

    async getMobileOverview(params = {}) {
      const resource = createResource('cdrMobileOverview', ctx, id);
      return resource.list(params);
    },

    async getMobileDataUsage(params = {}) {
      const resource = createResource('cdrMobileData', ctx, id);
      return resource.list(params);
    },

    // ===== Support =====
    async sendSupportRequest(data) {
      const resource = createResource('support', ctx);
      return resource.store({ ...data, client_id: id });
    },

    // ===== Failover (renvoi d'urgence) =====
    async updateFailover(number, data) {
      const endpoint = ctx
        ? getEndpoint('failoverUpdate', ctx, id, number)
        : getEndpoint('failoverUpdate', ctx, number);
      const resource = new Resource(endpoint);
      return resource.updateNoId(data);
    },

    async updateFailoverBatch(data) {
      const resource = createResource('failoverBatch', ctx, id);
      return resource.updateNoId(data);
    },

    // ===== GED Documents =====
    async getDocuments(params = {}) {
      const resource = createResource('documents', ctx, id);
      return resource.list(params);
    },

    async uploadDocument(formData) {
      const resource = createResource('documents', ctx, id);
      return resource.store(formData);
    },

    async updateDocument(docId, data) {
      const resource = createResource('documentItem', ctx, id, docId);
      return resource.updateNoId(data);
    },

    async deleteDocument(docId) {
      const resource = createResource('documents', ctx, id);
      return resource.destroy(docId);
    },

    getDocumentDownloadUrl(docId) {
      const endpoint = getEndpoint('documentDownload', ctx, id, docId);
      return `/api/${endpoint}`;
    },

    getDocumentPreviewUrl(docId) {
      const endpoint = getEndpoint('documentPreview', ctx, id, docId);
      return `/api/${endpoint}`;
    },

    getDocumentUploadUrl() {
      return `/api/${getEndpoint('documents', ctx, id)}`;
    },

    async getSoundFiles() {
      const resource = createResource('documentSoundFiles', ctx, id);
      return resource.list();
    },

    async getSharedSummaryForUnlink(childId) {
      const resource = createResource('documentSharedSummary', ctx, id, childId);
      return resource.list();
    },

    // ===== Helpers =====
    isExtranet() {
      return ctx;
    },

    getClientId() {
      return id;
    }
  };
}

/**
 * Factory pour créer une API groupe de clients
 * @param {number|string} groupId - ID du groupe
 * @param {boolean} isExtranet - true si contexte extranet
 * @returns {Object} API groupe avec méthodes unifiées
 */
export function createGroupApi(groupId, isExtranet = false) {
  const ctx = isExtranet;
  const id = groupId;

  return {
    async getUtils() {
      const resource = createResource('groupUtils', ctx, id);
      return resource.list();
    },

    async getReceipts(params = {}) {
      const resource = createResource('groupReceipts', ctx, id);
      return resource.list(params);
    },

    async getServices(params = {}) {
      const resource = createResource('groupServices', ctx, id);
      return resource.list(params);
    },

    async getSipAccounts(params = {}) {
      const resource = createResource('groupSipAccounts', ctx, id);
      return resource.list(params);
    },

    async getSipBillNames() {
      const resource = createResource('groupSipBillNames', ctx, id);
      return resource.list();
    },

    isExtranet() {
      return ctx;
    },

    getGroupId() {
      return id;
    }
  };
}

// Export des constantes pour référence
export { ENDPOINTS };

export default {
  createClientApi,
  createGroupApi,
  createResource,
  getEndpoint,
  isExtranetContext,
  ENDPOINTS
};
